import React, { useState, useEffect } from "react"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import Panel from "aldoo-ra/Panel"
import { CursorArrowRaysIcon } from "@heroicons/react/24/outline"
import { modifierDefinitions, propertyDefinitions } from "../data/property-definitions"

const ModifiersEditor = ({
  currentClass,
  onModifierChange
}) => {
  const group = propertyDefinitions.modifiers
  // Parse current class name to extract base name and modifier
  const parseClassName = (className) => {
    if (!className) return { baseName: '', modifier: '', modifierValue: '' }

    const parts = className.split(':')
    if (parts.length === 1) {
      return { baseName: parts[0], modifier: '', modifierValue: '' }
    }

    const baseName = parts[0]
    const modifierPart = parts[1]

    // Check for modifiers that need values (like nth-child(2n))
    const parenthesesMatch = modifierPart.match(/^(.+?)\((.+)\)$/)
    if (parenthesesMatch) {
      return {
        baseName,
        modifier: parenthesesMatch[1],
        modifierValue: parenthesesMatch[2]
      }
    }

    return { baseName, modifier: modifierPart, modifierValue: '' }
  }

  // Build class name from parts
  const buildClassName = (baseName, modifier, modifierValue) => {
    if (!baseName) return ''
    if (!modifier) return baseName

    const modifierDef = modifierDefinitions[modifier]
    if (modifierDef?.needsValue && modifierValue) {
      return `${baseName}:${modifier}(${modifierValue})`
    }

    return `${baseName}:${modifier}`
  }

  // Parse current class name to get modifier info
  const { baseName, modifier, modifierValue } = parseClassName(currentClass?.name || '')

  const [selectedModifier, setSelectedModifier] = useState(modifier || '')
  const [modifierValueInput, setModifierValueInput] = useState(modifierValue || '')

  // Update state when currentClass changes
  useEffect(() => {
    const parsed = parseClassName(currentClass?.name || '')
    setSelectedModifier(parsed.modifier || '')
    setModifierValueInput(parsed.modifierValue || '')
  }, [currentClass?.name])

  // Convert modifierDefinitions to dropdown options
  const modifierOptions = Object.entries(modifierDefinitions).map(([key, def]) => ({
    value: key,
    name: def.name,
    label: def.name,
    description: def.description
  }))

  const handleModifierChange = (newModifier) => {
    if (!newModifier || newModifier.length === 0) {
      setSelectedModifier('')
      setModifierValueInput('')
      // Remove modifier, go back to base class name
      const newClassName = baseName
      const classData = {
        ...currentClass,
        name: newClassName,
        properties: currentClass?.properties || {}
      }
      onModifierChange?.(classData)
      return
    }

    const modifier = newModifier[0]?.value || ''
    setSelectedModifier(modifier)

    const modifierDef = modifierDefinitions[modifier]

    // If this modifier doesn't need a value, update immediately
    if (!modifierDef?.needsValue) {
      setModifierValueInput('')
      const newClassName = buildClassName(baseName, modifier, '')
      const classData = {
        ...currentClass,
        name: newClassName,
        properties: currentClass?.properties || {}
      }
      onModifierChange?.(classData)
    } else {
      // Keep existing value or set default
      const defaultValue = modifier === 'nth-child' ? '1' : modifier === 'nth-of-type' ? '1' : ''
      const value = modifierValueInput || defaultValue
      setModifierValueInput(value)
      const newClassName = buildClassName(baseName, modifier, value)
      const classData = {
        ...currentClass,
        name: newClassName,
        properties: currentClass?.properties || {}
      }
      onModifierChange?.(classData)
    }
  }

  const handleModifierValueChange = (e) => {
    const value = e.target.value
    setModifierValueInput(value)

    const newClassName = buildClassName(baseName, selectedModifier, value)
    onModifierChange?.({
      ...currentClass,
      name: newClassName,
      properties: currentClass?.properties || {}
    })
  }

  const selectedModifierDef = modifierDefinitions[selectedModifier]
  const needsValue = selectedModifierDef?.needsValue

  // Check if any modifier is selected (like hasAnyValue in other editors)
  const hasAnyModifier = selectedModifier && selectedModifier !== ''

  const titleElement = (
    <div className="flex items-center space-x-2">
      <CursorArrowRaysIcon className="w-4 h-4 text-admin_text_secondary" />
      <Typography variant="admin_body_small" className={`font-medium ${hasAnyModifier ? 'text-admin_primary' : 'text-admin_text'}`}>
        {group?.label || 'CSS Modifiers'}
      </Typography>
    </div>
  )

  return (
    <Panel
      variant="admin_bordered"
      defaultOpen={false}
      collapsible={true}
      scrollable={false}
      animated={true}
      title={titleElement}
      styleOverrides={{
        panel: "border-0 border-b border-admin_bordered rounded-none bg-admin_paper",
        header: "px-4 py-3 bg-admin_paper hover:bg-admin_neutral_1 transition-colors h-[50px] flex items-center justify-between",
        body: "p-0 bg-admin_canvas"
      }}
    >
      <div className="px-4 pb-4">
        <div className="ml-6 space-y-3">
          <Typography variant="admin_body_small" className="text-admin_text_secondary">
            Add pseudo-classes like :hover, :active, :focus to your CSS class
          </Typography>

          <div className="space-y-2">
            <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
              Modifier
            </Typography>
            <DropDownSelect
              variant="admin"
              options={modifierOptions}
              value={selectedModifier ? [modifierOptions.find(opt => opt.value === selectedModifier)] : []}
              onChange={handleModifierChange}
              placeholder="Select modifier..."
              multiSelection={false}
              showCheckbox={false}
              showSelectionTick={true}
              searchEnabled={true}
              className="w-full"
              onItemRender={(option) => (
                <div>
                  <div className="font-medium">{option.name}</div>
                  <div className="text-xs text-admin_text_secondary">{option.description}</div>
                </div>
              )}
              onValueItemRender={(option) => {
                // Handle null/empty state
                if (!option) return "Select modifier..."

                // Return simple string for selected value display
                return option.name || option.label || "Unknown modifier"
              }}
            />
          </div>

          {needsValue && (
            <div className="space-y-2">
              <Typography variant="admin_body_small" className="font-medium capitalize text-admin_text_secondary text-sm">
                Value
              </Typography>
              <Input
                variant="admin"
                value={modifierValueInput}
                onChange={handleModifierValueChange}
                placeholder={
                  selectedModifier === 'nth-child' ? 'e.g. 2n, odd, even, 3' :
                  selectedModifier === 'nth-of-type' ? 'e.g. 2n, odd, even, 3' :
                  selectedModifier === 'not' ? 'e.g. .excluded-class, [disabled]' :
                  'Enter value...'
                }
                className="w-full"
              />
              <Typography variant="admin_body_small" className="text-admin_text_secondary text-xs">
                {selectedModifier === 'nth-child' && 'Examples: 1 (first), 2n (even), 2n+1 (odd), 3n (every third)'}
                {selectedModifier === 'nth-of-type' && 'Examples: 1 (first), 2n (even), 2n+1 (odd), 3n (every third)'}
                {selectedModifier === 'not' && 'Examples: .class-name, #id, [attribute], :hover'}
              </Typography>
            </div>
          )}

          {selectedModifier && (
            <div className="bg-admin_neutral_1 p-3 rounded">
              <Typography variant="admin_body_small" className="font-medium text-admin_text mb-1 text-xs">
                Generated CSS Selector:
              </Typography>
              <code className="text-sm font-mono text-admin_primary">
                .{buildClassName(baseName, selectedModifier, modifierValueInput)}
              </code>
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}

export default ModifiersEditor